<template>
  <div v-if="value">
    <PortableTextVue :value="value" :components="components" />
  </div>
</template>

<script lang="ts" setup>
import { PortableText as PortableTextVue } from '@portabletext/vue'
import { components } from './portable-components'
import type { GQLBlock } from '@core/types/generated/text'
import type { GQLImage } from '@core/types/generated/image'
import { nanoid } from 'nanoid'

const props = defineProps<{
  content: Array<GQLBlock | GQLImage> | null | undefined
}>()

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g
const INVALID_PLACEHOLDER_REGEX = new RegExp(INVALID_PLACEHOLDER_VALUE, 'g')

const value = computed(() => {
  const content = props.content
    ?.map((v) => (v._type === 'Block' ? transformBlock(v) : v))
    .filter(isNotNull)
  if (!content?.length) {
    return null
  }
  return content
})

function transformBlock(block: GQLBlock): GQLBlock | null {
  if (!block.children || block.children.every((c) => !c.text)) {
    return null
  }

  const newBlock: GQLBlock = { ...block, markDefs: [...(block.markDefs ?? [])] }
  const transformedChildren = block.children
    .flatMap((child: NonNullable<GQLBlock['children']>[number]) => {
      if (child.text && MARKDOWN_LINK_PATTERN.test(child.text)) {
        const parts = []
        let currentIndex = 0

        child.text.replace(
          MARKDOWN_LINK_PATTERN,
          (match, placeholderText: string, href: string, offset: number) => {
            if (offset > currentIndex) {
              parts.push({
                ...child,
                _key: nanoid(),
                text: child.text?.slice(currentIndex, offset),
                marks: child.marks || [],
              })
            }

            const marksId = nanoid()
            parts.push({
              _key: nanoid(),
              _type: 'span',
              marks: [marksId],
              text: placeholderText,
            })

            newBlock.markDefs?.push({
              _key: marksId,
              _type: 'link',
              targetBlank: true,
              href,
            })

            currentIndex = offset + match.length
            return match
          },
        )

        if (currentIndex < child.text.length) {
          parts.push({
            ...child,
            _key: nanoid(),
            text: child.text.slice(currentIndex),
            marks: child.marks || [],
          })
        }

        return parts
      }
      return [child]
    })
    .flatMap(replaceInvalidPlaceholders)

  newBlock.children = transformedChildren
  return newBlock
}

function replaceInvalidPlaceholders(child: NonNullable<GQLBlock['children']>[number]) {
  if (child.text && INVALID_PLACEHOLDER_REGEX.test(child.text)) {
    const parts = []
    let currentIndex = 0

    child.text.replace(INVALID_PLACEHOLDER_REGEX, (match, offset) => {
      if (offset > currentIndex) {
        parts.push({
          ...child,
          _key: nanoid(),
          text: child.text?.slice(currentIndex, offset),
          marks: child.marks || [],
        })
      }

      parts.push({ _key: nanoid(), _type: 'InvalidPlaceholder' })

      currentIndex = offset + match.length
      return match
    })

    if (currentIndex < child.text.length) {
      parts.push({
        ...child,
        _key: nanoid(),
        text: child.text.slice(currentIndex),
        marks: child.marks || [],
      })
    }

    return parts
  }

  return [child]
}
</script>
